import { NeckDiagramStudentView, NeckDiagramHandedness } from "../neckDiagrams/NeckDiagramStudentView.js";
import { NeckDiagramLibraryPicker } from "../neckDiagrams/NeckDiagramLibraryPicker.js";
import { LessonNeckDiagramPanel } from "../LessonNeckDiagramPanel.js";
import type { LessonPracticeReview } from "@wmf/shared/practiceReview";
import { assignLessonRepertoire } from "../repertoire.js";
import { CurrentLessonPractice, LessonPracticeSide, useLessonPracticeReview } from "./LessonPracticeReview.js";
import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { Suspense, lazy, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { lessonNoteLink, lessonNoteParts, removeLessonNoteLinks } from "@wmf/shared/notes";
import { ApiError, get, send, type Attendance, type BookingLesson, type LessonEmailDelivery, type Lesson } from "../api.js";
import { attendanceToSave, type AmeliaAttendance } from "../booking/lessonOutcome.js";
import { LessonAttendance } from "./LessonAttendance.js";
import { LessonCreditAction } from "./LessonCreditAction.js";
import { formatDate, formatTime } from "../format.js";
import { LessonNoteAttachments } from "../LessonNoteAttachments.js";
import { LessonResourceEditor, type LessonResourceMatch } from "../LessonResourceEditor.js";
import { addLessonResource } from "../lessonResourceQuickAdd.js";
import { MoveLesson } from "../MoveLesson.js";
import { useAsync } from "../useAsync.js";
import { LessonNotesTools } from "./LessonNotesTools.js";
import { LessonStudentIdentity } from "./LessonStudentIdentity.js";
import { LessonStudentWorkPanel } from "./LessonStudentWorkPanel.js";
import { LessonStudentGoalsPanel } from "./LessonStudentGoalsPanel.js";
import { NoteActivityPanel } from "../engagement/ActivityPanels.js";
import { publishedLessonDiaryState } from "../calendar/lessonPublication.js";
import { isOwner, useOptionalAuth } from "../auth.js";
import { inboxApi } from "../inbox/api.js";
import "../privateLessonNotes.css";
import {
  publishPrivateLessonRecoveryStatus,
  readPrivateLessonDraft,
  removePrivateLessonDraft,
  writePrivateLessonDraft,
  type PrivateLessonDraft,
} from "../privateLessonDraft.js";
import "../lessonNotes.css";

// The engraver/groove editor is a large optional surface. Keep it out of the
// diary/lesson-notes bundle until somebody actually works with music.
const LessonGrooves = lazy(() =>
  import("../LessonGroovePanel.js").then((m) => ({ default: m.LessonGrooves })),
);
const InlineLessonGrooveBuilder = lazy(() =>
  import("../InlineLessonGrooveBuilder.js").then((m) => ({
    default: m.InlineLessonGrooveBuilder,
  })),
);

const InlineNeckDiagramBuilder = lazy(() =>
  import("../InlineNeckDiagramBuilder.js").then((m) => ({ default: m.InlineNeckDiagramBuilder })),
);

/**
 * Attendance that is only ever a note. "Did not attend" and "Cancelled" change
 * the booking and its money, so they come from Amelia (see LessonAttendance)
 * and are never chosen here.
 */
const ATTENDANCE: Array<{ value: Attendance | ""; label: string }> = [
  { value: "", label: "Not recorded" },
  { value: "present", label: "Attended" },
  { value: "late", label: "Late" },
];

export interface LessonNotesReturnTarget {
  to: string;
  label: string;
}

export function lessonNotesReturnTarget(state: unknown): LessonNotesReturnTarget {
  if (state && typeof state === "object") {
    const candidate = state as Record<string, unknown>;
    const to = candidate["returnTo"];
    const label = candidate["returnLabel"];
    if (typeof to === "string" && to.startsWith("/") && !to.startsWith("//")) {
      return {
        to,
        label: typeof label === "string" && label.trim() !== "" ? label.trim() : "Previous page",
      };
    }
  }
  return { to: "/diary", label: "Diary" };
}

export function studentLessonContextHref(
  studentId: string,
  tab: "practice" | "progress",
  scope: "all" | "mine",
  returnTo: string,
): string {
  const params = new URLSearchParams({ tab });
  if (scope === "mine") params.set("scope", "mine");
  params.set("return", returnTo);
  return `/students/${encodeURIComponent(studentId)}?${params.toString()}`;
}

export function nextLessonBookingHref(
  studentId: string,
  scope: "all" | "mine",
  returnTo: string,
): string {
  const params = new URLSearchParams({ tab: "lessons", book: "1" });
  if (scope === "mine") params.set("scope", "mine");
  params.set("return", returnTo);
  return `/students/${encodeURIComponent(studentId)}?${params.toString()}`;
}

interface LessonReadResult {
  scope: string;
  lesson: BookingLesson | null;
  failure: { status: number | null; message: string } | null;
}

export function LessonNotes() {
  const { bookingId = "" } = useParams();
  const currentUser = useOptionalAuth()?.me ?? null;
  const accountId = currentUser?.user.id ?? null;
  const scopeKey = `${accountId ?? "anonymous"}:${bookingId}`;
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const currentBooking = useRef(bookingId);
  currentBooking.current = bookingId;
  const bookingGeneration = useRef({ id: bookingId, version: 0 });
  if (bookingGeneration.current.id !== bookingId) {
    bookingGeneration.current = { id: bookingId, version: bookingGeneration.current.version + 1 };
  }
  const generation = bookingGeneration.current.version;
  const isCurrentBooking = () => currentBooking.current === bookingId && bookingGeneration.current.version === generation;
  useEffect(() => {
    currentBooking.current = bookingId;
    return () => { currentBooking.current = ""; };
  }, [bookingId]);
  const location = useLocation();
  const navigate = useNavigate();
  const returnTarget = lessonNotesReturnTarget(location.state);
  // A failed read is returned as a result, not thrown, so it keeps its status
  // and stays on screen (with Retry disabled) while a retry is in flight. The
  // scope check below drops any result that belongs to another lesson/account.
  const lessonRead = useAsync<LessonReadResult>(
    async () => {
      try {
        return { scope: scopeKey, lesson: await get<BookingLesson>(`/admin/bookings/${bookingId}/lesson`), failure: null };
      } catch (cause) {
        return {
          scope: scopeKey,
          lesson: null,
          failure: {
            status: cause instanceof ApiError ? cause.status : null,
            message: cause instanceof Error ? cause.message : "Something went wrong.",
          },
        };
      }
    },
    [scopeKey],
  );
  const currentRead = lessonRead.data?.scope === scopeKey ? lessonRead.data : null;
  const data = currentRead?.lesson ?? null;
  const readFailure = currentRead?.failure ?? null;
  const { loading, reload } = lessonRead;
  const [keptDraft, setKeptDraft] = useState<PrivateLessonDraft | null>(null);
  useEffect(() => {
    // Look only: nothing is written or sent while the lesson cannot be read.
    if (!readFailure || !accountId) { setKeptDraft(null); return; }
    const stored = readPrivateLessonDraft(accountId, bookingId);
    setKeptDraft(stored.state === "available" ? stored.draft : null);
  }, [readFailure, accountId, bookingId]);

  const [showNeckPicker, setShowNeckPicker] = useState(false);
  const [showNeckBuilder, setShowNeckBuilder] = useState(false);
  const [neckRefresh, setNeckRefresh] = useState(0);
  const [emailDelivery, setEmailDelivery] = useState<LessonEmailDelivery | null>(null);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [familyConversationBusy, setFamilyConversationBusy] = useState(false);
  const [familyConversationProblem, setFamilyConversationProblem] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [attendance, setAttendance] = useState<Attendance | "">("");
  const [amelia, setAmelia] = useState<AmeliaAttendance | "unknown">("unknown");
  const [lessonStatusRevision, setLessonStatusRevision] = useState(0);
  const [saving, setSaving] = useState<"draft" | "publish" | "unpublish" | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  const [exists, setExists] = useState(false);
  const [lessonId, setLessonId] = useState<string | null>(null);
  const [editedFrom, setEditedFrom] = useState<string | null>(null);
  const [clash, setClash] = useState(false);
  const [showGrooveBuilder, setShowGrooveBuilder] = useState(false);
  const [grooveRefreshVersion, setGrooveRefreshVersion] = useState(0);
  const [resourceBusy, setResourceBusy] = useState(false);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [fileRefreshVersion, setFileRefreshVersion] = useState(0);
  const [filesDisclosureOpen, setFilesDisclosureOpen] = useState(false);
  const [filesNeedAttention, setFilesNeedAttention] = useState(false);
  const [musicDisclosureOpen, setMusicDisclosureOpen] = useState(false);
  const [musicAttention, setMusicAttention] = useState<Record<string, boolean>>({});
  const reportMusicAttention = useCallback((key: string, active: boolean) => {
    setMusicAttention((current) => current[key] === active ? current : { ...current, [key]: active });
  }, []);
  const musicNeedsAttention = Object.values(musicAttention).some(Boolean);
  const musicIsOpen = musicDisclosureOpen || musicNeedsAttention || showGrooveBuilder || showNeckBuilder || showNeckPicker;
  const editorRevision = useRef(0);
  const saveAttempt = useRef(0);
  const [editorVersion, setEditorVersion] = useState(0);
  const [acknowledgedVersion, setAcknowledgedVersion] = useState(0);
  const [draftPersisted, setDraftPersisted] = useState(false);
  const [recoveryDraft, setRecoveryDraft] = useState<PrivateLessonDraft | null>(null);
  const [recoveryNotice, setRecoveryNotice] = useState<string | null>(null);
  const [recoveryProblem, setRecoveryProblem] = useState<string | null>(null);
  const [unresolvedPracticeReviews, setUnresolvedPracticeReviews] = useState<PrivateLessonDraft["practiceReviews"]>([]);
  const [editorDataIdentity, setEditorDataIdentity] = useState<BookingLesson | null>(null);
  const [editorScope, setEditorScope] = useState<string | null>(null);
  const practice = useLessonPracticeReview({ domain: "private", bookingId, accountId, studentId: data?.booking.studentId ?? null }, markChanged);
  useEffect(() => {
    setResourceBusy(false);
    setAttachmentBusy(false);
    setSaving(null);
    setShowNeckPicker(false);
    setShowNeckBuilder(false);
    setShowGrooveBuilder(false);
    setFilesDisclosureOpen(false);
    setFilesNeedAttention(false);
    setMusicDisclosureOpen(false);
    setMusicAttention({});
    setAmelia("unknown");
    setFamilyConversationBusy(false);
    setFamilyConversationProblem(null);
  }, [scopeKey]);

  useEffect(() => {
    if (!data) return;
    saveAttempt.current += 1;
    editorRevision.current = 0;
    setEditorVersion(0);
    setAcknowledgedVersion(0);
    setUnresolvedPracticeReviews([]);
    setRecoveryProblem(null);
    setEmailDelivery(data.emailDelivery ?? null);
    setDeliveryError(null);
    setTitle(data.lesson?.title ?? data.booking.serviceName ?? "Lesson");
    setContent(data.lesson?.content ?? "");
    setAttendance(data.lesson?.attendanceStatus ?? "");
    setPublished(data.lesson?.publishedAt != null);
    setExists(data.lesson !== null);
    setLessonId(data.lesson?.id ?? null);
    setEditedFrom(data.lesson?.updatedAt ?? null);
    setSaved(null);
    setClash(false);
    setEditorDataIdentity(data);
    setEditorScope(scopeKey);
    if (!accountId) {
      setRecoveryDraft(null);
      setRecoveryNotice(null);
      return;
    }
    const stored = readPrivateLessonDraft(accountId, bookingId);
    if (stored.state === "available") {
      setRecoveryDraft(stored.draft);
      setRecoveryNotice(null);
    } else {
      setRecoveryDraft(null);
      setRecoveryNotice(
        stored.state === "expired"
          ? "An older lesson recovery draft expired and was removed. The saved lesson is unchanged."
          : stored.state === "invalid"
            ? "A damaged lesson recovery draft was removed. The saved lesson is unchanged."
            : stored.state === "unavailable"
              ? "This browser could not check for a recoverable lesson draft."
              : null,
      );
    }
  }, [data, accountId, bookingId, scopeKey]);

  const editorDirty = editorVersion > acknowledgedVersion || unresolvedPracticeReviews.length > 0;
  const recoveryNoteIsStale = recoveryDraft !== null && (
    recoveryDraft.baseRevision !== (data?.lesson?.updatedAt ?? null) ||
    recoveryDraft.lessonId !== (data?.lesson?.id ?? null)
  );
  const recoveryPracticeConflicts = recoveryDraft && !practice.loading
    ? practice.getConflictingDraftChanges(recoveryDraft.practiceReviews)
    : [];

  useEffect(() => {
    if (!data) {
      setDraftPersisted(false);
      publishPrivateLessonRecoveryStatus({ dirty: false, persisted: false });
      return;
    }

    if (editorDataIdentity !== data || editorScope !== scopeKey) return;

    if (!accountId) {
      setDraftPersisted(false);
      setRecoveryProblem(editorDirty ? "Sign-in could not be confirmed, so this browser cannot store your private lesson changes." : null);
      publishPrivateLessonRecoveryStatus({ dirty: editorDirty, persisted: false });
      return;
    }

    if (recoveryDraft) {
      const persisted = !editorDirty;
      setDraftPersisted(persisted);
      if (editorDirty) {
        setRecoveryProblem("Choose whether to restore or discard the saved draft before leaving these new edits.");
      } else setRecoveryProblem(null);
      publishPrivateLessonRecoveryStatus({ dirty: editorDirty, persisted });
      return;
    }

    if (!editorDirty) {
      setDraftPersisted(false);
      setRecoveryProblem(null);
      publishPrivateLessonRecoveryStatus({ dirty: false, persisted: true });
      return;
    }

    const reviewByTask = new Map(unresolvedPracticeReviews.map((review) => [review.todoId, review]));
    for (const review of practice.getChanges()) reviewByTask.set(review.todoId, review);
    const persisted = writePrivateLessonDraft({
      version: 1,
      accountId,
      domain: "private",
      bookingId,
      lessonId,
      baseRevision: editedFrom,
      savedAt: new Date().toISOString(),
      title,
      content,
      attendance,
      practiceReviews: [...reviewByTask.values()],
    });
    setDraftPersisted(persisted);
    setRecoveryProblem(persisted ? null : "This browser could not store your private lesson changes. Copy them before leaving or refreshing.");
    publishPrivateLessonRecoveryStatus({ dirty: true, persisted });
  }, [accountId, bookingId, data, editorDataIdentity, editorScope, scopeKey, editorDirty, recoveryDraft, editorVersion, acknowledgedVersion, lessonId, editedFrom, title, content, attendance, unresolvedPracticeReviews]);

  useEffect(() => {
    publishPrivateLessonRecoveryStatus({ dirty: editorDirty, persisted: draftPersisted });
    return () => publishPrivateLessonRecoveryStatus({ dirty: false, persisted: true });
  }, [editorDirty, draftPersisted]);

  useEffect(() => {
    if (!editorDirty || draftPersisted) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [editorDirty, draftPersisted]);

  useEffect(() => {
    if (!editorDirty || draftPersisted) return;
    const warnInternalNavigation = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest<HTMLAnchorElement>("a[href]");
      if (!link || link.target || link.hasAttribute("download")) return;
      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin) return;
      if (window.confirm("Your private lesson changes could not be saved in this tab. Leaving now will lose the unsaved title, notes, attendance and practice-review edits. Stay to copy them, or choose Leave to continue.")) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    document.addEventListener("click", warnInternalNavigation, true);
    return () => document.removeEventListener("click", warnInternalNavigation, true);
  }, [editorDirty, draftPersisted]);

  if (readFailure) {
    const denied = readFailure.status === 403;
    return (
      <section className="panel lesson-notes-read-failure" aria-labelledby="lesson-notes-read-failure-heading">
        <h1 id="lesson-notes-read-failure-heading">{keptDraft ? keptDraft.title : "Lesson notes"}</h1>
        <div className="error" role="alert">
          {denied ? "You do not have access to this lesson's notes. Nothing has been changed." : readFailure.message}
        </div>
        {keptDraft && (
          <p className="small" role="status">
            Your unsaved edits for this lesson are kept in this browser tab. They will be offered for restoring once the lesson loads.
          </p>
        )}
        <div className="row">
          {!denied && (
            <button type="button" onClick={reload} disabled={loading}>
              {loading ? "Retrying…" : "Retry"}
            </button>
          )}
          <Link to={returnTarget.to}>Back to {returnTarget.label}</Link>
        </div>
      </section>
    );
  }
  if (loading || !data) return <p className="empty">Loading lesson notes…</p>;

  const { booking } = data;
  const neckDiagramInstrument = data.neckDiagramInstrument;
  const radioValue: Attendance | "" = attendance === "present" || attendance === "late" ? attendance : "";
  const encodedStudentId = booking.studentId === null ? null : encodeURIComponent(booking.studentId);
  const studentReturnTarget =
    encodedStudentId !== null && returnTarget.to.startsWith(`/students/${encodedStudentId}`)
      ? returnTarget
      : null;
  const noteState = !exists ? "Not saved" : published ? "Published" : "Draft";
  const noteStateClass = !exists ? "" : published ? "is-published" : "is-draft";
  const noteStateCopy = !exists
    ? "This lesson does not have a note yet. Save a draft to keep your work private, or publish when it is ready for the family."
    : published
      ? "The family can see this note. Saving changes keeps it published."
      : "Only staff can see this draft. Publish it when it is ready for the family.";

  async function openFamilyConversation() {
    if (
      booking.studentId === null ||
      currentUser?.booking.staff !== true ||
      familyConversationBusy ||
      saving !== null ||
      (editorDirty && !draftPersisted)
    ) return;

    setFamilyConversationBusy(true);
    setFamilyConversationProblem(null);
    try {
      const { threadId } = await inboxApi.openStudentConversation(booking.studentId);
      if (!isCurrentBooking()) return;
      navigate(`/inbox/${encodeURIComponent(threadId)}`, {
        state: { inboxAccountId: accountId, inboxLessonId: published ? lessonId : null },
      });
    } catch (cause) {
      if (!isCurrentBooking()) return;
      setFamilyConversationProblem(
        cause instanceof ApiError && cause.status === 403
          ? "Access to this family conversation has changed. The lesson and its draft are unchanged."
          : cause instanceof Error
            ? cause.message
            : "Could not open the family conversation.",
      );
    } finally {
      if (isCurrentBooking()) setFamilyConversationBusy(false);
    }
  }

  async function refreshDelivery() {
    try { setEmailDelivery(await get<LessonEmailDelivery>(`/admin/bookings/${bookingId}/lesson/email-delivery`)); setDeliveryError(null); }
    catch { setDeliveryError("Email delivery could not be checked."); }
  }

  async function save(
    intent: "draft" | "publish" | "unpublish",
    rethrow = false,
  ): Promise<Lesson | null> {
    const savingRevision = editorRevision.current;
    const savingAttempt = ++saveAttempt.current;
    setSaving(intent);
    setSaved(null);
    setProblem(null);
    setClash(false);
    try {
      const result = await send<{ lesson: Lesson; practiceReview?: LessonPracticeReview }>(
        "PUT",
        `/admin/bookings/${bookingId}/lesson`,
        {
          title,
          content: content.trim() === "" ? null : content,
          attendanceStatus: attendanceToSave(amelia, attendance),
          ...(intent === "draft" ? {} : { published: intent === "publish" }),
          updatedAt: editedFrom,
          practiceReviews: practice.getChanges(),
          publishPracticeReviews: intent === "publish",
        },
      );
      if (!isCurrentBooking() || savingAttempt !== saveAttempt.current) return result.lesson;
      practice.accept(result.practiceReview);
      setPublished(result.lesson.publishedAt !== null);
      setExists(true);
      setLessonId(result.lesson.id);
      setEditedFrom(result.lesson.updatedAt);
      setAcknowledgedVersion(savingRevision);
      if (editorRevision.current === savingRevision && unresolvedPracticeReviews.length === 0 && accountId) {
        const removed = removePrivateLessonDraft(accountId, bookingId);
        setDraftPersisted(false);
        if (!removed) setRecoveryProblem("The saved recovery copy could not be cleared after this save.");
      }
      setSaved(
        intent === "publish"
          ? "Published — the family can see this now."
          : intent === "unpublish"
            ? "Withdrawn. Back to a draft."
            : published ? "Changes saved. Task reviews remain private until you publish task feedback." : "Draft saved. Not visible to the family.",
      );
      if (intent === "publish" && result.lesson.publishedAt !== null && editorRevision.current === savingRevision && unresolvedPracticeReviews.length === 0) {
        navigate("/diary", { state: publishedLessonDiaryState(booking) });
      }
      // Do not refetch after the first save. The response already contains the
      // new lesson id/version; refetching here used to clear the confirmation
      // and could replace text typed while the save request was in flight.
      return result.lesson;
    } catch (cause: unknown) {
      if (!isCurrentBooking() || savingAttempt !== saveAttempt.current) { if (rethrow) throw cause; return null; }
      if (cause instanceof ApiError && cause.code === "stale") setClash(true);
      const message = cause instanceof Error ? cause.message : "Could not save.";
      setProblem(message);
      if (rethrow) throw cause instanceof Error ? cause : new Error(message);
      return null;
    } finally {
      if (isCurrentBooking() && savingAttempt === saveAttempt.current) setSaving(null);
    }
  }

  async function ensureLesson(): Promise<string | null> {
    if (lessonId) return lessonId;
    const lesson = await save("draft", true);
    return lesson?.id ?? null;
  }

  async function quickAdd(resource: LessonResourceMatch, key: string) {
    const targetId = await ensureLesson();
    if (!isCurrentBooking()) throw new Error("The selected lesson changed.");
    if (!targetId) throw new Error("Could not create the lesson draft.");
    const result = await addLessonResource("private", targetId, resource, key);
    if (!isCurrentBooking()) return result;
    if (resource.kind === "groove") setGrooveRefreshVersion((version) => version + 1);
    else setFileRefreshVersion((version) => version + 1);
    return result;
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (saving !== null || resourceBusy || attachmentBusy) return;
    void save("draft");
  }

  function markChanged() {
    editorRevision.current += 1;
    setEditorVersion(editorRevision.current);
    setSaved(null);
  }

  function confirmLeaving(): boolean {
    if (!editorDirty || draftPersisted) return true;
    return window.confirm("Your private lesson changes could not be saved in this tab. Leaving now will lose the unsaved title, notes, attendance and practice-review edits. Stay to copy them, or choose Leave to continue.");
  }

  function discardRecoveryDraft() {
    if (accountId) {
      const removed = removePrivateLessonDraft(accountId, bookingId);
      if (!removed) setRecoveryProblem("The saved recovery draft could not be cleared from this browser tab.");
    }
    setRecoveryDraft(null);
    setUnresolvedPracticeReviews([]);
    setRecoveryNotice("The saved recovery draft was discarded. The server lesson is unchanged.");
  }

  function restoreRecoveryDraft() {
    if (!recoveryDraft) return;
    const conflicts = practice.getConflictingDraftChanges(recoveryDraft.practiceReviews);
    const conflictIds = new Set(conflicts.map((review) => review.todoId));
    const restorable = recoveryDraft.practiceReviews.filter((review) => !conflictIds.has(review.todoId));
    const missingTaskIds = practice.restoreChanges(restorable);
    const missingTaskSet = new Set(missingTaskIds);
    const unresolved = recoveryDraft.practiceReviews.filter((review) => conflictIds.has(review.todoId) || missingTaskSet.has(review.todoId));
    setUnresolvedPracticeReviews(unresolved);
    setTitle(recoveryDraft.title);
    setContent(recoveryDraft.content);
    setAttendance(recoveryDraft.attendance);
    setEditedFrom(recoveryDraft.baseRevision);
    setRecoveryDraft(null);
    setRecoveryProblem(null);
    setRecoveryNotice(
      recoveryNoteIsStale
        ? "Recovered changes are open for comparison. This draft is based on an older saved lesson version and cannot replace the newer server version silently."
        : unresolved.length > 0
          ? `${unresolved.length} practice-review change${unresolved.length === 1 ? " was" : "s were"} not restored because the task changed or could not be checked. They remain in this recovery draft until you discard them.`
          : "Recovered the saved lesson edits. They remain private until you use the existing Save draft or Publish action.",
    );
    markChanged();
  }

  function loadCurrentServerVersion() {
    if (accountId) removePrivateLessonDraft(accountId, bookingId);
    setRecoveryDraft(null);
    setUnresolvedPracticeReviews([]);
    setRecoveryNotice(null);
    setRecoveryProblem(null);
    reload();
  }

  return (
    <NeckDiagramStudentView studentId={booking.studentId}>
    <div className="lesson-notes-page">
      <p className="small muted lesson-notes-back">
        <Link to={returnTarget.to}>← {returnTarget.label}</Link>
      </p>

      <header className="lesson-notes-head">
        <div className="lesson-notes-title-row">
          <div>
            <p className="lesson-notes-kicker">Lesson notes</p>
            {booking.studentId ? (
              <LessonStudentIdentity
                kind="private"
                studentId={booking.studentId}
                studentName={booking.studentName ?? "Student"}
              />
            ) : (
              <h1>{booking.studentName ?? "Unmatched booking"}</h1>
            )}
            <p className="lede">Write the lesson up while the details are still fresh.</p>
          </div>
          <span className={`lesson-notes-state ${noteStateClass}`.trim()}>{noteState}</span>
        </div>

        <div className="lesson-notes-context" aria-label="Lesson details">
          <span className="lesson-notes-context-item">
            {formatDate(booking.startsAt)} · {formatTime(booking.startsAt)}
          </span>
          {booking.serviceName && (
            <span className="lesson-notes-context-item">{booking.serviceName}</span>
          )}
        </div>

        <MoveLesson
          bookingId={booking.bookingId}
          studentName={booking.studentName}
          onMoved={reload}
        />
      </header>

      {booking.studentId === null && (
        <div className="error">
          This booking is not matched to a student yet, so there is nowhere to put the notes. Say
          who it is for above, or <Link to="/queue">work through the queue</Link>.
        </div>
      )}

      {problem && <div className="error">{problem}</div>}
      {recoveryProblem && <div className="error" role="alert">{recoveryProblem}</div>}
      {recoveryDraft && (
        <section className="panel lesson-notes-recovery" aria-labelledby="lesson-notes-recovery-heading">
          <h2 id="lesson-notes-recovery-heading">Unsaved lesson edits are available</h2>
          <p className="small">This tab saved private lesson text, the attendance note and task-review edits. Restore them after checking this lesson, or discard the saved copy.</p>
          {recoveryNoteIsStale && <p className="small" role="alert">The saved lesson changed after this recovery draft was made. Restoring it opens the older edits for comparison; the server will reject a save based on an older revision.</p>}
          {practice.loading && recoveryDraft.practiceReviews.length > 0 && <p className="small muted">Checking the saved task reviews before restoring them…</p>}
          {!practice.loading && recoveryPracticeConflicts.length > 0 && <p className="small" role="alert">{recoveryPracticeConflicts.length} task-review change{recoveryPracticeConflicts.length === 1 ? " has" : "s have"} a different server version or could not be matched. Those changes will be kept in this recovery copy and will not be applied to the current task.</p>}
          {recoveryNoteIsStale && data.lesson && <details>
            <summary className="small">Current saved lesson preview</summary>
            <p className="small"><strong>{data.lesson.title}</strong></p>
            <pre className="lesson-notes-recovery-preview">{data.lesson.content ?? "No note text is saved."}</pre>
          </details>}
          <div className="row">
            <button type="button" onClick={restoreRecoveryDraft} disabled={practice.loading && recoveryDraft.practiceReviews.length > 0}>Restore saved edits</button>
            <button type="button" className="quiet" onClick={discardRecoveryDraft}>Discard recovery</button>
          </div>
        </section>
      )}
      {recoveryNotice && <div className="panel" role="status">
        <p className="small">{recoveryNotice}</p>
        {unresolvedPracticeReviews.length > 0 && <button type="button" className="quiet small" onClick={() => setUnresolvedPracticeReviews([])}>Discard changed task-review edits</button>}
      </div>}

      {clash && (
        <div className="panel lesson-notes-conflict">
          <strong>Someone else saved this lesson while you were editing.</strong>
          <p className="small">
            What you wrote is still here, unsaved. Copy anything you need, then load their version
            — or open this lesson in another tab to compare first.
          </p>
          <div className="row">
            <button type="button" className="quiet" onClick={loadCurrentServerVersion}>
              Load their version
            </button>
            <span className="small muted">This replaces what is currently in the boxes.</span>
          </div>
        </div>
      )}

      {booking.studentId && (
        <LessonNotesTools
          key={`tools:${bookingId}`}
          compactDisclosure
          studentId={booking.studentId}
          studentName={booking.studentName ?? "Student"}
          currentStartsAt={booking.startsAt}
          lessonId={lessonId}
          instrument={booking.serviceName ?? null}
          localLessonNoteFormattingEnabled={data.localLessonNoteFormattingEnabled === true}
          noteContent={content}
          onApplyFormattedSummary={(formatted) => {
            if (!isCurrentBooking()) return;
            setContent(formatted);
            markChanged();
          }}
          lessonDraftSaved={lessonId !== null && !editorDirty && saving === null}
          ensureNote={ensureLesson}
          onPracticeChanged={practice.reload}
          showPreviousPractice={true}
          onCopyPreviousNote={(previousContent) => {
            const source = previousContent.trim();
            if (!source || !isCurrentBooking() || published || saving !== null || resourceBusy || attachmentBusy) return;
            setContent((current) => `${current}${current.trim() ? "\n\n" : ""}${source}`);
            setProblem(null);
            markChanged();
          }}
          canCopyPreviousNote={!published}
          copyPreviousNoteDisabled={saving !== null || resourceBusy || attachmentBusy}
          disabled={saving !== null || resourceBusy || attachmentBusy}
          onResourceBusyChange={(busy) => { if (isCurrentBooking()) setResourceBusy(busy); }}
          onAddRepertoirePiece={(pieceTitle) => {
            setContent((current) => current.split(/\r?\n/).includes(pieceTitle)
              ? current : `${current}${current.trim() ? "\n\n" : ""}${pieceTitle}\n`);
            markChanged();
          }}
          onAddRepertoireAsset={async (resource, key) => {
            const added = await quickAdd(resource, key);
            if (!isCurrentBooking()) return;
            const link = lessonNoteLink(added.title, added.href);
            setContent((current) => lessonNoteParts(current).some((part) => part.href === added.href)
              ? current : `${current}${current && !current.endsWith("\n") ? "\n" : ""}${link}\n`);
            markChanged();
          }}
          onAssignRepertoire={async (selection, key) => {
            const targetId = await ensureLesson();
            if (!isCurrentBooking()) throw new Error("The selected lesson changed.");
            if (!targetId) throw new Error("Could not create the lesson draft.");
            const result = await assignLessonRepertoire(targetId, selection, key);
            if (isCurrentBooking()) {
              setContent(current => current.split(/\r?\n/).includes(result.title) ? current : `${current}${current.trim() ? "\n\n" : ""}${result.title}\n`);
              markChanged(); practice.reload();
            }
            return result;
          }}
        />
      )}

      {booking.studentId && currentUser?.booking.staff === true && (
        <LessonStudentGoalsPanel
          key={"student-goals:" + booking.studentId}
          studentId={booking.studentId}
          progressHref={studentLessonContextHref(
            booking.studentId,
            "progress",
            isOwner(currentUser) ? "all" : "mine",
            location.pathname + location.search,
          )}
        />
      )}

      {booking.studentId && (
        <LessonStudentWorkPanel
          key={`student-work:${booking.studentId}`}
          studentId={booking.studentId}
        />
      )}

      <div className="private-writeup-layout">
        {booking.studentId && (
          <section className="private-writeup-review">
            <CurrentLessonPractice state={practice} busy={saving !== null || resourceBusy || attachmentBusy} />
          </section>
        )}

        <form id="private-writeup-form" className="lesson-notes-form private-writeup-editor" onSubmit={onSubmit}>
          <section className="panel lesson-notes-editor" aria-labelledby="lesson-note-editor-heading">
            <div className="lesson-notes-editor-head">
              <div>
                <h2 id="lesson-note-editor-heading">Lesson write-up</h2>
                <a className="small" href="/help/write-and-publish-private-lesson-notes" target="_blank" rel="noreferrer">Help with lesson notes</a>
                <p className="small muted">
                  Record what the student worked on and the clearest thing to practise next.
                </p>
              </div>
            </div>

            <div className="lesson-notes-field">
              <label htmlFor="title">Title</label>
              <input
                id="title"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  markChanged();
                }}
                required
              />
            </div>

            <div className="lesson-notes-field">
              <label htmlFor="content">Notes and practice</label>
              <p className="small muted lesson-notes-helper" id="lesson-note-content-help">
                Include a song or exercise, the section worked on, and one specific next target.
              </p>
              <LessonResourceEditor
                id="content"
                value={content}
                onChange={(next) => {
                  setContent(next);
                  markChanged();
                }}
                describedBy="lesson-note-content-help"
                resourceContext={{ context: "private", bookingId }}
                onAdd={quickAdd}
                onBusyChange={setResourceBusy}
                disabled={saving !== null || attachmentBusy || !booking.studentId}
              />
            </div>

            <LessonCreditAction key={`credit:${bookingId}`} booking={booking} disabled={saving !== null} onChanged={() => setLessonStatusRevision(revision => revision + 1)} />
            <LessonAttendance
              key={bookingId}
              ameliaAppointmentId={booking.ameliaAppointmentId ?? null}
              ameliaBookingId={booking.ameliaBookingId ?? null}
              startsAt={booking.startsAt}
              studentName={booking.studentName ?? "this student"}
              serviceName={booking.serviceName}
              disabled={saving !== null}
              onAttendance={setAmelia}
              refreshKey={lessonStatusRevision}
            />
            {amelia === "active" && (attendance === "absent" || attendance === "cancelled") && (
              <p className="small muted">
                This note said {attendance === "absent" ? "“Did not attend”" : "“Cancelled”"}, but
                the booking system shows the lesson as booked. Saving clears this attendance note.
              </p>
            )}

            <fieldset className="lesson-notes-attendance">
              <legend>Attendance note</legend>
              <div className="lesson-notes-attendance-grid">
                {ATTENDANCE.map((option) => (
                  <label
                    className={`lesson-notes-attendance-option${radioValue === option.value ? " is-selected" : ""}`}
                    key={option.value || "unrecorded"}
                  >
                    <input
                      type="radio"
                      name="attendance"
                      value={option.value}
                      checked={radioValue === option.value}
                      onChange={(event) => {
                        setAttendance(event.target.value as Attendance | "");
                        markChanged();
                      }}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </section>
        </form>

        {booking.studentId && (
          <details
            className="private-writeup-disclosure private-writeup-music"
            open={musicIsOpen}
            onClick={(event) => {
              if (musicNeedsAttention && event.target instanceof Element && event.target.closest("summary")) {
                event.preventDefault();
              }
            }}
            onToggle={(event) => {
              if (!event.currentTarget.open && musicNeedsAttention) {
                event.currentTarget.open = true;
                return;
              }
              setMusicDisclosureOpen(event.currentTarget.open);
            }}
          >
            <summary>Music from this lesson <span className="private-writeup-disclosure-hint small muted">Grooves and practice instructions</span></summary>
            <div className="private-writeup-music-content">
              {neckDiagramInstrument && <NeckDiagramHandedness />}
              <div className="lesson-notes-grooves-head">
                <p className="small muted">
                  Attach an existing groove or create one to send notation with the lesson note.
                </p>
                <div className="row">
                  <button type="button" className="secondary" onClick={() => setShowGrooveBuilder(true)} disabled={saving !== null || resourceBusy || attachmentBusy}>
                    + Create new groove
                  </button>
                  {neckDiagramInstrument && (
                    <>
                      <button type="button" className="secondary" onClick={() => setShowNeckBuilder(true)} disabled={saving !== null || resourceBusy || attachmentBusy}>
                        Add neck diagram
                      </button>
                      <button type="button" className="secondary" onClick={() => setShowNeckPicker(true)} disabled={saving !== null || resourceBusy || attachmentBusy}>
                        Choose saved neck diagram
                      </button>
                    </>
                  )}
                </div>
              </div>

              {lessonId ? (
                <Suspense fallback={<div className="panel">Loading grooves…</div>}>
                  <LessonGrooves
                    key={grooveRefreshVersion}
                    lessonId={lessonId}
                    studentId={booking.studentId}
                    visible={musicIsOpen}
                    onAttentionChange={reportMusicAttention}
                    onPracticeChanged={practice.reload}
                    onBusyChange={setResourceBusy}
                  />
                </Suspense>
              ) : (
                <p className="small muted">
                  You can create a groove now. The lesson draft will be made automatically when a groove needs a lesson to attach to.
                </p>
              )}
              {lessonId && <LessonNeckDiagramPanel key={`${lessonId}:${neckRefresh}`} lessonId={lessonId} />}
            </div>
          </details>
        )}

        {booking.studentId && (
          <details
            className="private-writeup-disclosure private-writeup-files"
            open={filesDisclosureOpen || filesNeedAttention || attachmentBusy}
            onClick={(event) => {
              if ((filesNeedAttention || attachmentBusy) && event.target instanceof Element && event.target.closest("summary")) {
                event.preventDefault();
              }
            }}
            onToggle={(event) => {
              if (!event.currentTarget.open && (filesNeedAttention || attachmentBusy)) {
                event.currentTarget.open = true;
                return;
              }
              setFilesDisclosureOpen(event.currentTarget.open);
            }}
          >
            <summary>Files and attachments</summary>
            <LessonNoteAttachments
              key={bookingId}
              refreshVersion={fileRefreshVersion}
              noteId={lessonId}
              basePrefix={`/admin/students/${encodeURIComponent(booking.studentId)}/lessons`}
              storageConfigured={data.storageConfigured === true}
              ensureNote={ensureLesson}
              onNoteCreated={(createdLessonId) => {
                setLessonId(createdLessonId);
                setExists(true);
              }}
              onRemoved={(result) => {
                setContent((current) => removeLessonNoteLinks(current, result.removedHref));
                // Retain conflict protection if another writer changed the note before removal.
                setEditedFrom((current) => current === result.previousUpdatedAt ? result.updatedAt : current);
                markChanged();
              }}
              onBusyChange={setAttachmentBusy}
              onAttentionChange={setFilesNeedAttention}
              headingId="lesson-note-files-heading"
              libraryFor={`student:${booking.studentId}`}
              practiceTarget={{ domain: "private", learnerId: booking.studentId }}
              onPracticeChanged={practice.reload}
              disabled={saving !== null || resourceBusy || attachmentBusy}
            />
          </details>
        )}

        {/* After the editor, music and files in reading and tab order: on a phone
            the tutor finishes writing and the Save and Publish actions are the
            next thing, not a screen above. The grid places this in the side
            column on wide screens whatever its position here. */}
        <div className="lesson-writeup-status-track private-writeup-status-track">
          <aside className="lesson-notes-side" aria-label="Lesson note status and actions">
            <div className="panel lesson-notes-status-card">
              <h2>Note status</h2>
              <span className={`lesson-notes-state ${noteStateClass}`.trim()}>{noteState}</span>
              <p className="small muted lesson-notes-status-copy">{noteStateCopy}</p>

              {published && emailDelivery && <div className="small">
                <p>Email delivery: {emailDelivery.total === 0 ? "No email queued." : `${emailDelivery.queued + emailDelivery.sending} queued, ${emailDelivery.sent} sent, ${emailDelivery.delivered} delivered, ${emailDelivery.failed + emailDelivery.bounced + emailDelivery.complained} failed or bounced.`}</p>
                <button type="button" className="quiet small" onClick={() => void refreshDelivery()}>Refresh delivery</button>
                {deliveryError && <p role="alert">{deliveryError}</p>}
              </div>}
              <div className="lesson-notes-actions">
                {published ? (
                  <>
                    <button type="submit" form="private-writeup-form" className="lesson-notes-primary-action" disabled={saving !== null || resourceBusy || attachmentBusy || booking.studentId === null}>
                      {saving === "draft" ? "Saving…" : "Save changes"}
                    </button>
                    <button type="button" className="quiet" disabled={saving !== null || resourceBusy || attachmentBusy || unresolvedPracticeReviews.length > 0} onClick={() => void save("publish")}>{saving === "publish" ? "Publishing…" : "Publish task feedback"}</button>
                    <button type="button" className="quiet" disabled={saving !== null || resourceBusy || attachmentBusy} onClick={() => void save("unpublish")}>
                      {saving === "unpublish" ? "Withdrawing…" : "Withdraw to draft"}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="lesson-notes-primary-action" disabled={saving !== null || resourceBusy || attachmentBusy || booking.studentId === null || unresolvedPracticeReviews.length > 0} onClick={() => void save("publish")}>
                      {saving === "publish" ? "Publishing…" : "Publish to family"}
                    </button>
                    <button type="submit" form="private-writeup-form" className="quiet" disabled={saving !== null || resourceBusy || attachmentBusy || booking.studentId === null}>
                      {saving === "draft" ? "Saving…" : "Save draft"}
                    </button>
                  </>
                )}
                <button type="button" className="quiet" onClick={() => { if (confirmLeaving()) void navigate(returnTarget.to); }}>
                  Back to {returnTarget.label}
                </button>
              </div>

              {booking.studentId !== null && currentUser?.booking.staff === true && (
                <nav className="lesson-notes-actions" aria-label="Lesson next steps">
                  <button
                    type="button"
                    className="quiet"
                    disabled={familyConversationBusy || saving !== null || (editorDirty && !draftPersisted)}
                    onClick={() => void openFamilyConversation()}
                  >
                    {familyConversationBusy ? "Opening Inbox…" : "Message family"}
                  </button>
                  {familyConversationProblem && <p className="error" role="alert">{familyConversationProblem}</p>}
                  <Link
                    className="button quiet"
                    to={studentLessonContextHref(
                      booking.studentId,
                      "practice",
                      isOwner(currentUser) ? "all" : "mine",
                      `${location.pathname}${location.search}`,
                    )}
                  >
                    Review practice and student work
                  </Link>
                  <Link
                    className="button quiet"
                    to={studentLessonContextHref(
                      booking.studentId,
                      "progress",
                      isOwner(currentUser) ? "all" : "mine",
                      `${location.pathname}${location.search}`,
                    )}
                  >
                    Review goals and progress
                  </Link>
                  <Link
                    className="button quiet"
                    to={nextLessonBookingHref(
                      booking.studentId,
                      isOwner(currentUser) ? "all" : "mine",
                      `${location.pathname}${location.search}`,
                    )}
                  >
                    Book next lesson
                  </Link>
                </nav>
              )}

              {saved && <ActionConfirmation variant="success" title="Lesson notes saved" description={saved} />}
            </div>

            <LessonPracticeSide state={practice} busy={saving !== null || resourceBusy || attachmentBusy}
              allowWithdraw={!editorDirty && !recoveryDraft}
              onWithdrawBusyChange={(next) => { if (isCurrentBooking() && currentScope.current === scopeKey) setResourceBusy(next); }} />
            <details className="private-writeup-disclosure private-writeup-guidance">
              <summary>Recent activity and writing guidance</summary>
              {lessonId && <NoteActivityPanel showFeedback={false} domain="private" noteId={lessonId} revision={editedFrom} />}
              <div className="panel">
                <h2 className="lesson-notes-checklist-title">A useful note usually has</h2>
                <ul className="small lesson-notes-checklist">
                  <li>what you worked on today</li>
                  <li>one clear practice target</li>
                  <li>a song, section or tempo when it matters</li>
                </ul>
              </div>
            </details>
          </aside>
        </div>
      </div>

      {showNeckPicker && neckDiagramInstrument && <NeckDiagramLibraryPicker
        ensureLesson={ensureLesson}
        onAdded={attachedLessonId => { if (!isCurrentBooking()) return; setLessonId(attachedLessonId); setExists(true); setNeckRefresh(version => version + 1); setShowNeckPicker(false); }}
        onClose={() => { if (isCurrentBooking()) setShowNeckPicker(false); }} />}
      {showNeckBuilder && neckDiagramInstrument && (
        <Suspense fallback={<p>Opening neck diagram editor…</p>}>
          <InlineNeckDiagramBuilder
            lessonId={lessonId}
            ensureLesson={ensureLesson}
            instrument={neckDiagramInstrument}
            onAdded={(attachedLessonId) => {
              if (!isCurrentBooking()) return;
              setLessonId(attachedLessonId);
              setExists(true);
              setNeckRefresh((version) => version + 1);
              setShowNeckBuilder(false);
            }}
            onClose={() => { if (isCurrentBooking()) setShowNeckBuilder(false); }}
          />
        </Suspense>
      )}

      {showGrooveBuilder && booking.studentId && (
        <Suspense fallback={<div className="panel">Opening Groove Builder…</div>}>
          <InlineLessonGrooveBuilder
            lessonId={lessonId}
            ensureLesson={ensureLesson}
            onAdded={(attachedLessonId) => {
              if (!isCurrentBooking()) return;
              setLessonId(attachedLessonId);
              setExists(true);
              setGrooveRefreshVersion((version) => version + 1);
              setShowGrooveBuilder(false);
            }}
            onClose={() => { if (isCurrentBooking()) setShowGrooveBuilder(false); }}
          />
        </Suspense>
      )}

      {encodedStudentId && (
        <p className="small muted lesson-notes-student-link">
          <Link to={studentReturnTarget?.to ?? `/students/${encodedStudentId}`}>
            {studentReturnTarget ? `Back to ${studentReturnTarget.label} →` : "See everything for this student →"}
          </Link>
        </p>
      )}
    </div>
    </NeckDiagramStudentView>
  );
}
